// Nested documents in a @craft-form/renderer payload.
//
// A relation-table holds its rows as an array of objects under the table's own
// key, and a file-upload cell inside a row stores the picked `File` itself
// (the renderer forces `uploadMode: "deferred"` for table columns, since this
// app configures no per-field `dataProvider.uploadFile`). The proposal save
// splits only the payload's TOP-LEVEL keys into file parts vs JSON, so a row's
// `File` went into the JSON branch and `JSON.stringify` flattened it to `{}` —
// the document vanished while every top-level document uploaded fine.
//
// Fix: hoist each nested `File` into its own multipart part, named by its full
// path in the payload (`<tableKey>.<rowIndex>.<attributeKey>`). The backend
// stores a part name verbatim as the media row's `mappedattributekey`
// (varchar(200)) against the proposal form, and on read merges every media row
// back into the saved JSON under that same key — so `restoreNestedFiles` below
// puts it back inside its row before the renderer ever sees the record. No
// backend change is involved.
//
// Both functions are no-ops for a payload with no nested `File` and no such
// path key — a form that has no relation-table, or an older one saved before
// any of this, comes through byte-identical (the same object REFERENCES, even,
// so nothing downstream can see a difference).

// Only a plain object is safe to rebuild. Anything carrying its own `toJSON`
// (a dayjs/moment date, for one) or its own prototype (File, Blob, Date, any
// class instance) serializes through that method today, and copying its
// enumerable keys into a bare object would silently change what gets saved.
const isPlainObject = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  if (typeof value.toJSON === "function") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

// A hoisted path always carries a numeric row segment; a plain attribute key
// never contains a dot, so this cannot collide with a real form field.
const NESTED_PATH = /\.\d+(\.|$)/;

export const isNestedFilePath = (key) => NESTED_PATH.test(key);

/**
 * Returns `value` with every nested `File` removed, each one collected into
 * `files` under its dotted path. Top-level `File`s are the caller's business —
 * this is for what the caller was about to `JSON.stringify`. Returns `value`
 * itself, unrebuilt, when there was nothing to hoist.
 */
export const hoistNestedFiles = (value, path, files) => {
  if (value instanceof File) {
    files[path] = value;
    return undefined;
  }

  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item, index) => {
      const kept = hoistNestedFiles(item, `${path}.${index}`, files);
      if (kept !== item) changed = true;
      return kept;
    });
    return changed ? next : value;
  }

  if (!isPlainObject(value)) return value;

  let changed = false;
  const next = {};
  for (const [key, inner] of Object.entries(value)) {
    const kept = hoistNestedFiles(inner, `${path}.${key}`, files);
    if (kept !== inner) changed = true;
    if (kept !== undefined) next[key] = kept;
  }
  return changed ? next : value;
};

// The container a path's last segment lives in, or `undefined` if the path
// leads nowhere in this record — an unrelated key that merely looks like a
// path, or a row deleted since its document was saved. Nothing is invented and
// nothing is dropped in that case: the key is left exactly where it is.
const findContainer = (root, segments) => {
  let node = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    if (Array.isArray(node)) {
      const index = Number(segment);
      if (!Number.isInteger(index) || index < 0 || index >= node.length) {
        return undefined;
      }
      node = node[index];
    } else if (node && typeof node === "object") {
      if (!(segment in node)) return undefined;
      node = node[segment];
    } else {
      return undefined;
    }
  }
  return node && typeof node === "object" ? node : undefined;
};

// A cell that already holds its own reference wins over the merged-in one.
// The row keeps the reference it was saved with, so it survives a reorder or a
// row delete; the media rows are keyed by the row INDEX the document was first
// uploaded at, which after either of those points at a different row.
const isEmptyCell = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (isPlainObject(value) && Object.keys(value).length === 0);

// Copy-on-write assignment: the record comes from a query cache / Redux, so
// nothing already in it is mutated.
const assignAtPath = (container, segments, value) => {
  const [head, ...rest] = segments;

  if (Array.isArray(container)) {
    const index = Number(head);
    if (!rest.length) {
      if (!isEmptyCell(container[index])) return container;
      const next = container.slice();
      next[index] = value;
      return next;
    }
    const next = container.slice();
    next[index] = assignAtPath(next[index], rest, value);
    return next;
  }

  if (rest.length === 0) {
    return isEmptyCell(container[head])
      ? { ...container, [head]: value }
      : container;
  }

  return { ...container, [head]: assignAtPath(container[head], rest, value) };
};

/**
 * Moves every `<tableKey>.<rowIndex>.<attributeKey>` entry the API merged in at
 * the top level back into the row it belongs to, dropping the flat key.
 * `formatValue` reshapes the API's file reference into what the renderer's
 * file-upload field reads (name/url). A key whose path leads nowhere is left
 * untouched, so no record can lose a value to this.
 */
export const restoreNestedFiles = (record, formatValue) => {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return record;
  }

  let result = record;
  for (const key of Object.keys(record)) {
    if (!isNestedFilePath(key)) continue;

    const segments = key.split(".");
    if (!findContainer(result, segments)) continue;

    const value = result[key];
    const placed = assignAtPath(
      result,
      segments,
      formatValue ? formatValue(value) : value,
    );
    const { [key]: _flatKey, ...withoutFlatKey } = placed;
    result = withoutFlatKey;
  }
  return result;
};
